import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { Models, ShopDisplayType } from "@/api/types";
import { findBuyXGetYRewardDiscount } from "services/HelperService";

dayjs.extend(utc);
dayjs.extend(timezone);

type RewardGiftCardExpiryTerms = Pick<
  Models<"DiscountModel">,
  "rewardGiftCardExpiresAfterDays" | "rewardGiftCardExpiresOn"
>;

/** A moment's calendar day, as a plain date so formatting it never shifts it. */
const calendarDay = (moment: Dayjs): Dayjs => dayjs(moment.format("YYYY-MM-DD"));

/**
 * Today on the practice's calendar: the date in the practice's time zone, or on the patient's device
 * when the practice has none because its locations span more than one.
 */
export const practiceToday = (timeZoneId: string | null, now: Dayjs): Dayjs =>
  calendarDay(timeZoneId ? now.tz(timeZoneId) : now);

/** Whether an offer counts its reward's expiry in days, so showing it needs today's date. */
export const countsRewardExpiryInDays = (terms: RewardGiftCardExpiryTerms): boolean =>
  !terms.rewardGiftCardExpiresOn && terms.rewardGiftCardExpiresAfterDays != null;

/**
 * The last day a buy X get Y offer's free reward gift card can be spent when the order is paid
 * today: the offer's fixed date, or today plus the offer's days, the same day the server stamps at
 * payment. `today` is today on the practice's calendar, undefined while it is not known yet, when a
 * days-based expiry has no day to show. Null when the offer's reward never expires.
 */
export const resolveRewardGiftCardExpiryDay = (
  terms: RewardGiftCardExpiryTerms,
  today: Dayjs | undefined,
): Dayjs | null => {
  if (terms.rewardGiftCardExpiresOn) {
    return dayjs(terms.rewardGiftCardExpiresOn);
  }
  if (terms.rewardGiftCardExpiresAfterDays != null && today) {
    return today.add(terms.rewardGiftCardExpiresAfterDays, "day");
  }
  return null;
};

/**
 * The last day the gift card on an order line can be spent if the order is paid today, by the
 * server's rule for crediting it as expiring value: the line is made entirely free, and the buy X
 * get Y offer that gives it as a reward sets an expiry. Null for every other line, including a
 * reward card the buyer pays toward, which never expires.
 */
export const resolveOrderLineRewardExpiryDay = (
  line: OrderLine,
  today: Dayjs | undefined,
): Dayjs | null => {
  const rewardTerms = findOrderLineRewardExpiryTerms(line);
  return rewardTerms ? resolveRewardGiftCardExpiryDay(rewardTerms, today) : null;
};

type OrderLine = Pick<
  Models<"ShopDisplayOrderLineItemModel">,
  "displayType" | "cost" | "discountTotal" | "discounts" | "package"
>;

/**
 * The expiry terms of the offer that makes an order line's gift card its free reward: the line is
 * made entirely free, and the buy X get Y offer that gives it sets an expiry. Undefined otherwise.
 */
const findOrderLineRewardExpiryTerms = (line: OrderLine): RewardGiftCardExpiryTerms | undefined => {
  const isFreeGiftCard =
    line.displayType === ShopDisplayType.GiftCard &&
    line.discountTotal != null &&
    line.discountTotal >= line.cost;
  if (!isFreeGiftCard) {
    return undefined;
  }

  return findBuyXGetYRewardDiscount(line.package?.id, line.discounts);
};

/** Whether an order line is a free reward gift card whose offer sets an expiry. */
export const orderLineHasRewardExpiry = (line: OrderLine): boolean =>
  findOrderLineRewardExpiryTerms(line) !== undefined;

/** Whether an order line's free reward gift card counts its expiry in days, so showing it needs today. */
export const orderLineCountsRewardExpiryInDays = (line: OrderLine): boolean => {
  const rewardTerms = findOrderLineRewardExpiryTerms(line);
  return rewardTerms ? countsRewardExpiryInDays(rewardTerms) : false;
};

/**
 * The last day value expiring at `expiresOnUtc` can be spent, on the practice's calendar (the
 * device's when the practice has no time zone). The server stores the moment the expiry day ends in
 * the practice location's time zone, the following midnight, so the day is the one just before it.
 */
export const lastDayBeforeExpiry = (expiresOnUtc: string, timeZoneId: string | null): Dayjs => {
  const lastMoment = dayjs(expiresOnUtc).subtract(1, "millisecond");
  return calendarDay(timeZoneId ? lastMoment.tz(timeZoneId) : lastMoment);
};

/** Whether value expiring at `expiresOnUtc` has expired by `now`, as the server's expiry decides. */
export const hasPromotionalValueExpired = (expiresOnUtc: string, now: Dayjs): boolean =>
  !now.isBefore(dayjs(expiresOnUtc));

/** A free promotional reward card's last day, and whether that day has passed. */
export interface WalletGiftCardPromotionalExpiry {
  expiryDay: Dayjs;
  expired: boolean;
}

/**
 * How a wallet gift card shows its promotional expiry: a free reward card that expired before it
 * was delivered shows the day it expired; one credited as expiring value shows its last day, or
 * the day it expired once that has passed. Undefined for a purchased card, which never expires.
 */
export const resolveWalletGiftCardPromotionalExpiry = (
  card: Pick<Models<"DisplayGiftCardDetails">, "expiresOnUtc" | "expiredUnusedOnUtc">,
  now: Dayjs,
  timeZoneId: string | null,
): WalletGiftCardPromotionalExpiry | undefined => {
  if (card.expiredUnusedOnUtc) {
    return { expiryDay: lastDayBeforeExpiry(card.expiredUnusedOnUtc, timeZoneId), expired: true };
  }
  if (card.expiresOnUtc) {
    return {
      expiryDay: lastDayBeforeExpiry(card.expiresOnUtc, timeZoneId),
      expired: hasPromotionalValueExpired(card.expiresOnUtc, now),
    };
  }
  return undefined;
};

/** Whether a wallet gift card shows a promotional expiry, so showing it needs the practice's time zone. */
export const hasWalletGiftCardPromotionalExpiry = (
  card: Pick<Models<"DisplayGiftCardDetails">, "expiresOnUtc" | "expiredUnusedOnUtc">,
): boolean => Boolean(card.expiredUnusedOnUtc || card.expiresOnUtc);
